import React, { useEffect, useState } from 'react';
import axiosInstance from '../../utils/axiosInstance';

const VendorList = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedVendor, setExpandedVendor] = useState(null);
  const [grandTotals, setGrandTotals] = useState({ items: 0, amount: 0 });

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const response = await axiosInstance.get('/auth/registerlistvendors');
        const vendorData = response?.data?.vendors || [];
        setVendors(vendorData);
        const items = vendorData.reduce((s, v) => s + (v.totalSoldItems || 0), 0);
        const amount = vendorData.reduce((s, v) => s + (v.totalSalesAmount || 0), 0);
        setGrandTotals({ items, amount });
      } catch (error) {
        console.error('Error fetching vendors:', error);
        setVendors([]);
      } finally {
        setLoading(false);
      }
    };
    fetchVendors();
  }, []);

  const filtered = vendors.filter(v =>
    (v.businessName || '').toLowerCase().includes(search.toLowerCase()) ||
    (v.email || '').toLowerCase().includes(search.toLowerCase())
  );

  const formatCurrency = n =>
    '₹' + (n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

  const getInitials = name =>
    (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  const getAddress = vendor =>
    [vendor?.address?.street, vendor?.address?.city, vendor?.address?.state, vendor?.address?.country]
      .filter(Boolean).join(', ') || '—';

  return (
    <div className="vendors-page">

      {/* Page header */}
      <div className="ph">
        <div>
          <h1>Vendors</h1>
          <p>{vendors.length} registered vendors</p>
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { label: 'Total Vendors', val: vendors.length },
          { label: 'Total Items Sold', val: grandTotals.items.toLocaleString() },
          { label: 'Total Revenue', val: formatCurrency(grandTotals.amount) },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding: '12px 18px', flex: '1', minWidth: 130 }}>
            <div style={{ fontSize: 10, color: 'var(--tx2)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.07em' }}>
              {s.label}
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--tx)', marginTop: 5 }}>
              {s.val}
            </div>
          </div>
        ))}
      </div>

      {/* Search toolbar */}
      <div className="toolbar">
        <div className="search-box">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
          </svg>
          <input
            placeholder="Search by name or email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && <button className="clear-btn" onClick={() => setSearch('')}>✕</button>}
        </div>
        <span style={{ fontSize: 12, color: 'var(--tx2)', flexShrink: 0 }}>
          {filtered.length} of {vendors.length} vendors
        </span>
      </div>

      {/* Table with horizontal scroll */}
      <div className="tbl-wrap">
        <div className="tbl-scroll">

          {loading && (
            <div className="loading-state">
              <div className="spinner" />
              <p>Fetching vendors…</p>
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="empty-state">
              <i className="fas fa-store" style={{ fontSize: 28, color: 'var(--bdr)' }} />
              <p>{search ? 'No vendors match your search.' : 'No vendors found.'}</p>
            </div>
          )}

          {!loading && filtered.length > 0 && (
            <table className="tbl" style={{ minWidth: 860 }}>
              <thead>
                <tr>
                  <th style={{ width: 36 }}>#</th>
                  <th>Vendor</th>
                  <th>Contact</th>
                  <th>Address</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'center' }}>Items Sold</th>
                  <th style={{ textAlign: 'center' }}>Revenue</th>
                  <th style={{ textAlign: 'center' }}>Products</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((vendor, i) => {
                  const isOpen = expandedVendor === vendor._id;

                  // ✅ API field: vendor.store === 'Store Open' (from uploaded file)
                  const storeOpen = vendor?.store === 'Store Open';
                  const soldItems = vendor?.totalSoldItems ?? 0;
                  const salesAmt = vendor?.totalSalesAmount ?? 0;
                  const productCount = vendor?.products?.length ?? 0;

                  return (
                    <React.Fragment key={vendor._id}>
                      <tr
                        style={{ cursor: 'pointer', background: isOpen ? '#FAFAFA' : undefined }}
                        onClick={() => setExpandedVendor(isOpen ? null : vendor._id)}
                      >
                        <td className="col-num">{i + 1}</td>

                        {/* Vendor name + avatar */}
                        <td>
                          <div className="vendor-cell">
                            {vendor?.logo?.url
                              ? <img src={vendor.logo.url} alt="" className="vendor-logo" />
                              : <div className="vendor-logo-fallback">{getInitials(vendor.businessName)}</div>
                            }
                            <div style={{ minWidth: 0 }}>
                              <div className="vendor-biz" title={vendor.businessName}>
                                {vendor.businessName || '—'}
                              </div>
                              <div className="vendor-email">{vendor.email || '—'}</div>
                            </div>
                          </div>
                        </td>

                        <td>
                          <span className="muted">{vendor.contactNumber || '—'}</span>
                        </td>

                        <td style={{ maxWidth: 180 }}>
                          <span className="muted" style={{ fontSize: 11, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>
                            {getAddress(vendor)}
                          </span>
                        </td>

                        <td>
                          <span className={`store-badge ${storeOpen ? 'store-open' : 'store-closed'}`}>
                            <span className="store-dot" />
                            {storeOpen ? 'Open' : 'Closed'}
                          </span>
                        </td>

                        {/* Always show numbers even if 0 */}
                        <td style={{ textAlign: 'center' }}>
                          <span className={`num-cell${soldItems === 0 ? ' zero' : ''}`}>
                            {soldItems.toLocaleString()}
                          </span>
                        </td>

                        <td style={{ textAlign: 'center' }}>
                          <span className={`num-cell${salesAmt === 0 ? ' zero' : ''}`}>
                            {formatCurrency(salesAmt)}
                          </span>
                        </td>

                        <td style={{ textAlign: 'center' }}>
                          <span className={`num-cell${productCount === 0 ? ' zero' : ''}`}>
                            {productCount}
                          </span>
                        </td>

                        {/* Stop row click from firing when chevron clicked */}
                        <td onClick={e => e.stopPropagation()}>
                          <button
                            className="chevron-btn"
                            onClick={() => setExpandedVendor(isOpen ? null : vendor._id)}
                            title="View products"
                          >
                            <svg
                              className={`vchevron${isOpen ? ' open' : ''}`}
                              width="13" height="13"
                              fill="none" stroke="currentColor" strokeWidth="2.5"
                              viewBox="0 0 24 24"
                            >
                              <path d="M6 9l6 6 6-6" />
                            </svg>
                          </button>
                        </td>
                      </tr>

                      {/* Expanded products panel */}
                      {isOpen && (
                        <tr className="vendor-expand-row">
                          <td colSpan="9" style={{ padding: 0 }}>
                            <div className="vendor-expand-inner">
                              <div className="vendor-expand-label">
                                Products ({productCount})
                              </div>
                              {productCount === 0 ? (
                                <p style={{ fontSize: 12, color: 'var(--tx3)', fontStyle: 'italic' }}>
                                  No products listed for this vendor.
                                </p>
                              ) : (
                                <div className="product-pills">
                                  {vendor.products.map(p => (
                                    <div key={p._id} className="product-pill">
                                      <span className="product-pill-name" title={p.name}>
                                        {p.name}
                                      </span>
                                      <span className="product-pill-price">
                                        ₹{(p.price || 0).toLocaleString()}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}

        </div>
      </div>
    </div>
  );
};

export default VendorList;